import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useState } from "react";
import useChatDocuments from "../../../../../hooks/agent/useChatDocuments";
import { Button, ButtonText } from "../../../../../ui/button";
import { Input, InputField } from "../../../../../ui/input";
import { Text } from "../../../../../ui/text";
import { VStack } from "../../../../../ui/vstack";
import { formatMoneyFromFragment } from "../../../../../utils/money";
import type { SelectOption } from "../../../wire";
import AmountInput from "./AmountInput";
import OptionCard from "./OptionCard";
import ProductCatalog from "./ProductCatalog";
import type { FieldControlProps } from "../fieldTypes";
import {
  isAskPriceError,
  isInvalidPrice,
  linkValue,
  productChoice,
} from "../formValues";
import { currencyDecimals } from "./moneyDigits";

/**
 * The card for the product already chosen from the catalogue, fetched by id — the
 * list it came from may be pages away, or (after a reload or an edit) not loaded at
 * all, so it cannot be looked up there.
 */
const ChosenProduct = ({
  productId,
  disabled,
  onClear,
}: {
  productId: string;
  disabled: boolean;
  onClear: () => void;
}) => {
  const documents = useChatDocuments();
  const { data } = useQuery(documents.productSelection, {
    variables: { id: productId },
  });
  const product = data?.product.get;
  const option: SelectOption = useMemo(
    () => ({
      value: productId,
      label: product?.name ?? "Producto elegido",
      description: product?.description ?? "",
      imageUrl: product?.image?.url ?? "",
      badge: product ? formatMoneyFromFragment(product.price) : "",
    }),
    [productId, product],
  );

  return (
    <VStack space="xs">
      <OptionCard
        option={option}
        selected
        disabled={disabled}
        onPress={onClear}
      />
      <Button
        action="muted"
        variant="link"
        size="xs"
        className="min-h-11 self-start"
        isDisabled={disabled}
        onPress={onClear}
      >
        <ButtonText>Cambiar producto</ButtonText>
      </Button>
    </VStack>
  );
};

/**
 * The product the campaign is about: one from the catalogue, or a pasted link the
 * backend imports on submit.
 *
 * Two ways in, one value. The catalogue list is `ProductCatalog`; the link is a plain
 * input that only counts once it has been left alone (validated on blur, so a half-typed address is never called wrong
 * mid-keystroke). Picking a product, or leaving a well-formed link, is the moment
 * `onCommitProduct` fires — the card then asks for whatever depends on it.
 *
 * Under a link the person can type the price, for pages that do not show one (the
 * backend refuses those otherwise); left empty, the page's own is used. The price
 * rides along in the value but is never part of what the options depend on — the
 * commit sends the link alone. Pointing the link at another page clears the price: it
 * was the old page's.
 */
const ProductField = ({
  field,
  value,
  onChange,
  disabled,
  invalid,
  error,
  onCommitProduct,
  onBlur,
}: FieldControlProps) => {
  const choice = productChoice(value);
  const [link, setLink] = useState(choice.kind === "link" ? choice.url : "");
  const [price, setPrice] = useState(
    choice.kind === "link" ? choice.price : "",
  );
  // The price box is red only when the price is what is wrong: a typed price that is
  // not above zero, or the backend asking for one. A bad link is the link's to show.
  const priceFlagged =
    invalid && (isInvalidPrice(price) || isAskPriceError(error));
  const precision = useMemo(
    () => ({
      decimals: currencyDecimals(field.currency),
      maxIntegerDigits: 14,
    }),
    [field.currency],
  );

  const pick = useCallback(
    (option: SelectOption) => {
      setLink("");
      setPrice("");
      onChange({ productId: option.value });
      onCommitProduct({ productId: option.value });
    },
    [onChange, onCommitProduct],
  );

  if (choice.kind === "catalogue") {
    return (
      <ChosenProduct
        productId={choice.productId}
        disabled={disabled}
        onClear={() => onChange(undefined)}
      />
    );
  }

  return (
    <VStack space="md">
      <ProductCatalog
        brandId={field.brandId}
        onPick={pick}
        disabled={disabled}
      />
      <VStack space="xs">
        <Text className="text-xs text-typography-500">
          …o pega el link de su página
        </Text>
        <Input
          size="md"
          isDisabled={disabled}
          isInvalid={invalid && !priceFlagged}
        >
          <InputField
            value={link}
            onChangeText={(text) => {
              // A price belongs to the page it was typed for; whitespace is not another page.
              const nextPrice = text.trim() === link.trim() ? price : "";
              setLink(text);
              setPrice(nextPrice);
              onChange(linkValue(text, nextPrice));
            }}
            onBlur={() => {
              onBlur();
              const trimmed = link.trim();
              if (trimmed) onCommitProduct({ url: trimmed });
            }}
            placeholder="https://"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            accessibilityLabel={`${field.label}: link de la página del producto`}
          />
        </Input>
        {link.trim() ? (
          <VStack space="xs" className="pt-1">
            <Text className="text-xs text-typography-500">Precio</Text>
            <AmountInput
              value={price}
              currency={field.currency}
              onChange={(digits) => {
                setPrice(digits);
                onChange(linkValue(link, digits));
              }}
              onBlur={onBlur}
              disabled={disabled}
              invalid={priceFlagged}
              precision={precision}
              accessibilityLabel={`${field.label}: precio del producto`}
            />
            <Text className="text-xs text-typography-500">
              Opcional. Si lo dejas vacío, usamos el de la página.
            </Text>
          </VStack>
        ) : null}
      </VStack>
    </VStack>
  );
};

export default ProductField;
